/**
 * cleo-web: Cleo Code's apps/web as a browser host on the World backend.
 *
 * Jason, 19 Oct 2026: "what would be the optimal way to create a new cleo-web app using the UI from cleo code as the
 * core". The answer is not a new app and not an extracted UI package: this app is already a browser single-page app that
 * Electron merely hosts, so cleo-web is a second host of the same screens (vite.web-host.config.ts), opened in an ordinary
 * browser with no Electron and no Mac server, reading the World the way Pocket does (the one-backend plan, 16 Oct 2026:
 * Desk, Pocket and Web each talk straight to the World). The page signs in with Clerk itself (WebHostWorldSession.tsx)
 * and reads the World through the window's own client (worldConvexClient.ts). The Desk routes the window can answer
 * from the World itself (the room index, a room entry, the board registry, a Canvas revision, the collapsed rails) it
 * answers in the Desk server's own shape (neucleos/webHostWorldDoors.ts); everything else the Desk reads through its
 * Mac server answers the one refusal below (environments/primary/fetch.ts), so each such screen says it needs the Mac.
 */

/** Pure: the host is the browser host when the build says so and no Electron preload bridge is in the window. */
export function webHostDecision(input: {
  readonly flag: string | undefined;
  readonly desktopBridge: unknown;
}): boolean {
  return input.flag === "world" && input.desktopBridge === undefined;
}

export function isWebHost(): boolean {
  if (typeof window === "undefined") return false;
  return webHostDecision({
    flag: import.meta.env.VITE_CLEO_WEB_HOST,
    desktopBridge: window.desktopBridge,
  });
}

/** The World authority the browser host reads: the deployment the Desk's server names by the same env (NEUCLEOS_CONVEX_URL). */
export function webHostConvexUrl(): string | null {
  const url = import.meta.env.VITE_WORLD_CONVEX_URL?.trim();
  return url ? url : null;
}

/** The Clerk token template the World verifies (the Desk's server names it the same way); "convex" when the build names none. */
export function webHostJwtTemplate(): string {
  return import.meta.env.VITE_WORLD_JWT_TEMPLATE?.trim() || "convex";
}

/** The one line a Mac-only screen says in the browser host. */
export const NEEDS_THE_MAC = "This needs the Cleo Mac app; open it there.";

/** The refusal kind every Desk route answers in the browser host; a screen that knows it says NEEDS_THE_MAC. */
export const NEEDS_THE_MAC_KIND = "needs_mac";

/**
 * The answer fetchPrimary gives in the browser host for any Desk route: there is no Mac server behind this page. One
 * shape for every route, so each screen's own failure path shows (and the World is never read as "not answering").
 */
export function needsTheMacResponse(path: string): Response {
  return new Response(
    JSON.stringify({ ok: false, error: NEEDS_THE_MAC_KIND, kind: NEEDS_THE_MAC_KIND, message: NEEDS_THE_MAC, path }),
    { status: 501, statusText: "Not Implemented", headers: { "content-type": "application/json" } },
  );
}
